// InterviewDetail.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callLarkProxy, LARK_TABLES } from "./larkService_v2";
import { toast } from "react-hot-toast";

function InterviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [formData, setFormData] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const fetchInterview = async () => {
      try {
        const result = await callLarkProxy("GET", `/tables/${LARK_TABLES.INTERVIEWS}/records/${id}`);
        if (result.data?.record) {
          const item = result.data.record;
          setInterview({
            id: item.record_id,
            candidateName: item.fields["Candidate Name"],
            userId: item.fields["User ID"],
            positionTitle: item.fields["Position Title"],
            resumeScore: item.fields["Resume Score"],
            interviewScore: item.fields["Interview Score"],
            finalScore: item.fields["Final Score"],
            status: item.fields["Status"],
            answers: item.fields["Answers JSON"] ? JSON.parse(item.fields["Answers JSON"]) : [],
            resumeUrl: item.fields["Resume URL"],
            videoUrl: item.fields["Video URL"],
            // Resilience: videos always upload to a predictable Cloudinary path
            // (interviews/{userId}/blob.webm). If the "Video URL" save to the
            // record failed but the file DID reach Cloudinary, reconstruct it so
            // the dashboard can still play it instead of showing "Video Syncing".
            videoFallbackUrl: item.fields["User ID"]
              ? `https://res.cloudinary.com/dedrxhoh7/video/upload/interviews/${item.fields["User ID"]}/blob.webm`
              : "",
            cheatingFlags: item.fields["Cheating Flags"],
            completedAt: item.fields["Completed At"],
            excelData: item.fields["Excel Data"],
            excelVideoUrl: item.fields["Excel Video"] || item.fields["Excel Video URL"],
            resumeDetails: item.fields["Resume Details"] ? JSON.parse(item.fields["Resume Details"]) : {},
            resumeStrengths: item.fields["Resume Strengths"] ? JSON.parse(item.fields["Resume Strengths"]) : [],
            resumeWeaknesses: item.fields["Resume Weaknesses"] ? JSON.parse(item.fields["Resume Weaknesses"]) : [],
            resumeFeedback: item.fields["Resume Feedback"],
          });
        }
      } catch (error) {
        console.error("Error fetching interview:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInterview();
  }, [id]);

  useEffect(() => {
    const fetchFormData = async () => {
      if (!interview?.userId) return;
      setLoadingForm(true);
      try {
        // Fetch all candidate records for this user to find the latest form
        const result = await callLarkProxy("GET", `/tables/${LARK_TABLES.CANDIDATE_INFO}/records?page_size=500`);
        
        if (result.data?.items) {
          // Filter by User ID OR Candidate Name (backup)
          const matchedRecords = result.data.items.filter(item => {
            const uid = item.fields["User ID"] || item.fields["userId"] || "";
            if (uid === interview.userId) return true;
            
            // Backup match by name inside JSON
            try {
              const formJson = item.fields["Form JSON"];
              if (formJson && interview.candidateName) {
                const parsed = JSON.parse(formJson);
                const formName = (parsed.name || parsed.personalDetails?.name || "").toLowerCase();
                return formName === interview.candidateName.toLowerCase();
              }
            } catch (_) {}
            return false;
          });

          if (matchedRecords.length > 0) {
            // Parse and Sort by createdAt in JSON
            const sortedRecords = matchedRecords.map(item => {
              let parsed = null;
              try {
                parsed = item.fields["Form JSON"] ? JSON.parse(item.fields["Form JSON"]) : item.fields;
              } catch (e) {
                parsed = item.fields;
              }
              return { ...item, parsedData: parsed };
            }).sort((a, b) => {
              const timeA = Number(a.parsedData?.createdAt) || 0;
              const timeB = Number(b.parsedData?.createdAt) || 0;
              return timeB - timeA; // Latest first
            });

            const latestItem = sortedRecords[0];
            setFormData(latestItem.parsedData);
            console.log("✅ Fetched latest candidate form:", latestItem.parsedData.name || "Unknown");
          } else {
            console.warn("⚠️ No candidate form found for User ID:", interview.userId);
          }
        }
      } catch (err) {
        console.error("Error fetching form data:", err);
      } finally {
        setLoadingForm(false);
      }
    };
    if (interview) fetchFormData();
  }, [interview]);

  const getScoreColor = (score) => {
    if (score >= 7) return "#10b981";
    if (score >= 4) return "#f59e0b";
    return "#ef4444";
  };

  const getScoreLabel = (score) => {
    if (score >= 7) return "Good";
    if (score >= 4) return "Average";
    return "Needs Improvement";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="detail-page">
        <div className="detail-loading">
          <div className="loading-spinner" />
          <p>Loading interview details...</p>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="detail-page">
        <div className="detail-empty">
          <h2>Interview Not Found</h2>
          <p>The interview record you're looking for doesn't exist.</p>
          <button onClick={() => navigate("/admin")} className="back-to-admin">
            ← Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  const answers = interview.answers || [];
  const resumeDetails = interview.resumeDetails || {};

  return (
    <div className="detail-page">
      <div className="detail-container">
        {/* Header */}
        <div className="admin-header" style={{ marginBottom: 24 }}>
          <div className="admin-header-left">
            <h1 style={{ color: "#1a2332", fontSize: "28px", margin: "0 0 4px", fontWeight: 800 }}>Interview Report</h1>
            <p className="detail-subtitle" style={{ fontSize: "15px", color: "#64748b", margin: 0 }}>
              Candidate: <strong style={{ color: "#374151" }}>{interview.userId || "Anonymous"}</strong>
              {" · "}
              {formatDate(interview.completedAt)}
            </p>
          </div>
          <button className="back-btn" onClick={() => navigate("/admin")}>
            ← Back to Candidates
          </button>
        </div>

        <div className="detail-header" style={{ paddingTop: 24, marginTop: 0 }}>
          <div className="detail-title-row">
            <div>
              <h2 style={{ margin: 0, fontSize: "20px", color: "#1a2332" }}>Performance Summary</h2>
            </div>
            <div className="detail-final-badge" style={{ borderColor: getScoreColor(interview.finalScore || 0) }}>
              <span className="final-value">{interview.finalScore || 0}</span>
              <span className="final-label">Final Score</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          <button
            className={`detail-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            📊 Overview
          </button>
          <button
            className={`detail-tab ${activeTab === "resume" ? "active" : ""}`}
            onClick={() => setActiveTab("resume")}
          >
            📄 Resume
          </button>
          <button
            className={`detail-tab ${activeTab === "answers" ? "active" : ""}`}
            onClick={() => setActiveTab("answers")}
          >
            💬 Answers ({answers.length})
          </button>
          <button
            className={`detail-tab ${activeTab === "candidateInfo" ? "active" : ""}`}
            onClick={() => setActiveTab("candidateInfo")}
          >
            👤 Candidate Info
          </button>
          <button
            className={`detail-tab ${activeTab === "video" ? "active" : ""}`}
            onClick={() => setActiveTab("video")}
          >
            🎥 Video
          </button>
          <button
            className={`detail-tab ${activeTab === "excelVideo" ? "active" : ""}`}
            onClick={() => setActiveTab("excelVideo")}
          >
            📊 Excel Video
          </button>
        </div>

        {/* Tab Content */}
        <div className="detail-content">
          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="overview-tab">
              {/* Score Cards */}
              <div className="detail-scores">
                <div className="detail-score-card">
                  <div className="detail-ring" style={{ borderColor: getScoreColor(interview.resumeScore || 0) }}>
                    <span className="ring-num">{interview.resumeScore || 0}</span>
                    <span className="ring-den">/10</span>
                  </div>
                  <h4>Resume Score</h4>
                  <span className="detail-badge" style={{ background: getScoreColor(interview.resumeScore || 0) }}>
                    {interview.resumeFeedback || getScoreLabel(interview.resumeScore || 0)}
                  </span>
                  <p className="score-weight">Weight: 40%</p>
                </div>

                <div className="detail-score-card">
                  <div className="detail-ring" style={{ borderColor: getScoreColor(interview.interviewScore || 0) }}>
                    <span className="ring-num">{interview.interviewScore || 0}</span>
                    <span className="ring-den">/10</span>
                  </div>
                  <h4>Interview Score</h4>
                  <span className="detail-badge" style={{ background: getScoreColor(interview.interviewScore || 0) }}>
                    {getScoreLabel(interview.interviewScore || 0)}
                  </span>
                  <p className="score-weight">Weight: 60%</p>
                </div>

                <div className="detail-score-card final-card">
                  <div className="detail-ring large" style={{ borderColor: getScoreColor(interview.finalScore || 0) }}>
                    <span className="ring-num">{interview.finalScore || 0}</span>
                    <span className="ring-den">/10</span>
                  </div>
                  <h4>Final Score</h4>
                  <span className="detail-badge" style={{ background: getScoreColor(interview.finalScore || 0) }}>
                    {getScoreLabel(interview.finalScore || 0)}
                  </span>
                  <p className="score-formula-text">Resume × 0.4 + Interview × 0.6</p>
                </div>
              </div>

              {/* Quick Info */}
              <div className="detail-info-grid">
                <div className="info-card">
                  <span className="info-icon">📝</span>
                  <div>
                    <strong>{interview.totalQuestions || answers.length}</strong>
                    <span>Total Questions</span>
                  </div>
                </div>
                <div className="info-card">
                  <span className="info-icon">📄</span>
                  <div>
                    <strong>{interview.resumeFileName || "No Resume"}</strong>
                    <span>Resume File</span>
                  </div>
                </div>
                <div className="info-card">
                  <span className="info-icon">🔑</span>
                  <div>
                    <strong>{resumeDetails.foundSkills?.length || 0} skills</strong>
                    <span>Detected in Resume</span>
                  </div>
                </div>
                <div className="info-card">
                  <span className="info-icon">📋</span>
                  <div>
                    <strong>{resumeDetails.foundSections?.length || 0}/4 sections</strong>
                    <span>Found in Resume</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RESUME TAB */}
          {activeTab === "resume" && (
            <div className="resume-tab">
              <div className="resume-detail-card">
                <div className="resume-detail-header">
                  <h3>📄 Resume Details</h3>
                  {interview.resumeUrl && (
                    <a
                      href={interview.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-resume-btn"
                    >
                      ⬇️ Download Resume
                    </a>
                  )}
                </div>

                <div className="resume-metrics">
                  <div className="metric">
                    <span className="metric-label">File Name</span>
                    <span className="metric-value">{interview.resumeFileName || "N/A"}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Word Count</span>
                    <span className="metric-value">{resumeDetails.wordCount || "N/A"}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Content Length Score</span>
                    <span className="metric-value">{resumeDetails.lengthScore || 0}/3</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Keyword Score</span>
                    <span className="metric-value">{resumeDetails.keywordScore || 0}/4</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Section Score</span>
                    <span className="metric-value">{resumeDetails.sectionScore || 0}/3</span>
                  </div>
                </div>

                {/* Sections Found */}
                <div className="resume-sections-detail">
                  <h4>Sections Detected</h4>
                  <div className="section-checks">
                    {["education", "skills", "projects", "experience"].map((section) => (
                      <div
                        key={section}
                        className={`section-check ${
                          resumeDetails.foundSections?.includes(section) ? "found" : "missing"
                        }`}
                      >
                        <span className="check-icon">
                          {resumeDetails.foundSections?.includes(section) ? "✅" : "❌"}
                        </span>
                        <span className="check-label">{section.charAt(0).toUpperCase() + section.slice(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Skills */}
                {resumeDetails.foundSkills?.length > 0 && (
                  <div className="resume-skills-detail">
                    <h4>Skills Detected</h4>
                    <div className="skill-tags-detail">
                      {resumeDetails.foundSkills.map((skill) => (
                        <span key={skill} className="skill-tag-detail">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Justification (Strengths & Weaknesses) */}
                <div className="ai-analysis-grid" style={{ marginBottom: 24 }}>
                  <div className="ai-analysis-card strengths">
                    <h4>✅ Strengths</h4>
                    <ul>
                      {(interview.resumeStrengths || []).length > 0 ? (
                        interview.resumeStrengths.map((s, i) => <li key={i}>{s}</li>)
                      ) : (
                        <li>Good overall structure and presentation.</li>
                      )}
                    </ul>
                  </div>
                  <div className="ai-analysis-card weaknesses">
                    <h4>⚠️ Areas for Improvement</h4>
                    <ul>
                      {(interview.resumeWeaknesses || []).length > 0 ? (
                        interview.resumeWeaknesses.map((w, i) => <li key={i}>{w}</li>)
                      ) : (
                        <li>No major weaknesses detected.</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Resume Score Summary */}
                <div className="resume-score-summary">
                  <div className="score-summary-circle" style={{ borderColor: getScoreColor(interview.resumeScore || 0) }}>
                    <span>{interview.resumeScore || 0}/10</span>
                  </div>
                  <div className="score-summary-text">
                    <strong style={{ color: getScoreColor(interview.resumeScore || 0) }}>
                      {interview.resumeFeedback || getScoreLabel(interview.resumeScore || 0)}
                    </strong>
                    <span>{interview.resumeFeedback ? "AI Justification Summary" : "Overall resume quality assessment"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ANSWERS TAB */}
          {activeTab === "answers" && (
            <div className="answers-tab">
              {answers.length === 0 ? (
                <div className="admin-empty">
                  <span className="empty-icon">📭</span>
                  <p>No answers recorded</p>
                </div>
              ) : (
                <div className="answers-detail-list">
                  {answers.map((a, idx) => (
                    <div key={idx} className="answer-detail-card">
                      <div className="answer-detail-header">
                        <div className="answer-q-number">Q{idx + 1}</div>
                        <div className="answer-score-badge" style={{ backgroundColor: getScoreColor(a.score || 0) }}>
                          {a.score || 0}/10
                        </div>
                      </div>
                      <div className="answer-detail-question">
                        <strong>Question:</strong>
                        <p>{a.question || a.Question}</p>
                      </div>
                      <div className="answer-detail-answer">
                        <strong>Answer:</strong>
                        <p>{a.answer || "(No answer provided)"}</p>
                      </div>
                      {a.feedback && (
                        <div className="answer-detail-feedback" style={{ 
                          marginTop: "12px", 
                          padding: "12px", 
                          backgroundColor: "#f0f9ff", 
                          borderRadius: "8px", 
                          border: "1px solid #e0f2fe",
                          fontSize: "14px",
                          color: "#0369a1",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px"
                        }}>
                          <span style={{ fontSize: "16px" }}>🤖</span>
                          <div>
                            <strong style={{ display: "block", marginBottom: "2px" }}>AI Feedback:</strong>
                            {a.feedback}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Average Answer Score */}
                  <div className="answers-summary">
                    <span>Average Answer Score:</span>
                    <strong style={{ color: getScoreColor(interview.interviewScore || 0) }}>
                      {interview.interviewScore || 0}/10
                    </strong>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CANDIDATE INFO TAB */}
          {activeTab === "candidateInfo" && (
            <div className="candidate-info-tab">
              {loadingForm ? (
                <div className="admin-empty">
                  <div className="loading-spinner" />
                  <p>Loading candidate information...</p>
                </div>
              ) : !formData ? (
                <div className="admin-empty">
                  <span className="empty-icon">👤</span>
                  <p>No extra information form found for this candidate.</p>
                </div>
              ) : (
                <div className="form-data-display">
                  {/* 1. Personal Details */}
                  <div className="display-section">
                    <h4>1. PERSONAL DETAILS</h4>
                    <div className="display-grid">
                      <div className="display-item"><span className="display-label">Name</span><span className="display-value">{formData.personalDetails?.name || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Mobile</span><span className="display-value">{formData.personalDetails?.mobile || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Emergency</span><span className="display-value">{formData.personalDetails?.emergency || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Email</span><span className="display-value">{formData.personalDetails?.email || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">DOB</span><span className="display-value">{formData.personalDetails?.dob || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Gender</span><span className="display-value">{formData.personalDetails?.gender || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Blood Group</span><span className="display-value">{formData.personalDetails?.bloodGroup || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Marital Status</span><span className="display-value">{formData.personalDetails?.maritalStatus || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Nationality</span><span className="display-value">{formData.personalDetails?.nationality || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Aadhar</span><span className="display-value">{formData.personalDetails?.aadhar || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">PAN</span><span className="display-value">{formData.personalDetails?.pan || "N/A"}</span></div>
                    </div>
                    <div className="display-item" style={{ marginTop: "12px" }}>
                      <span className="display-label">Permanent Address</span>
                      <span className="display-value">{formData.personalDetails?.permanentAddress || "N/A"}</span>
                    </div>
                  </div>

                  {/* 2. Bank Details */}
                  <div className="display-section">
                    <h4>2. BANK DETAILS</h4>
                    <div className="display-grid">
                      <div className="display-item"><span className="display-label">Bank Name</span><span className="display-value">{formData.bankDetails?.bankName || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Account Holder</span><span className="display-value">{formData.bankDetails?.accountHolder || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Account Number</span><span className="display-value">{formData.bankDetails?.accountNumber || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">IFSC Code</span><span className="display-value">{formData.bankDetails?.ifscCode || "N/A"}</span></div>
                    </div>
                  </div>

                  {/* 3. Identification */}
                  <div className="display-section">
                    <h4>3. IDENTIFICATION DETAILS</h4>
                    <div className="display-grid">
                      <div className="display-item"><span className="display-label">PF / UAN</span><span className="display-value">{formData.idDetails?.pfUan || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">ESIC Number</span><span className="display-value">{formData.idDetails?.esic || "N/A"}</span></div>
                      <div className="display-item"><span className="display-label">Driving License</span><span className="display-value">{formData.idDetails?.drivingLicense || "N/A"}</span></div>
                    </div>
                  </div>

                  {/* 4. Qualification */}
                  <div className="display-section">
                    <h4>4. QUALIFICATION DETAILS</h4>
                    <div className="table-responsive">
                      <table className="display-table">
                        <thead>
                          <tr>
                            <th>Qualification</th>
                            <th>Board/Uni</th>
                            <th>School/College</th>
                            <th>Subject</th>
                            <th>Year</th>
                            <th>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.qualifications?.map((q, i) => (
                            <tr key={i}>
                              <td>{q.qualification}</td>
                              <td>{q.board}</td>
                              <td>{q.school}</td>
                              <td>{q.subject}</td>
                              <td>{q.passingYear}</td>
                              <td>{q.percentage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 5. Additional Details */}
                  <div className="display-section">
                    <h4>5. ADDITIONAL DETAILS</h4>
                    <div className="display-grid two-col">
                      <div className="display-item">
                        <span className="display-label">Awards / Scholarships</span>
                        <ul style={{ margin: "5px 0", fontSize: "13px" }}>
                          {formData.additionalDetails?.awards?.filter(a => a).map((a, i) => <li key={i}>{a}</li>)}
                          {(!formData.additionalDetails?.awards?.some(a => a)) && <li>N/A</li>}
                        </ul>
                      </div>
                      <div className="display-item">
                        <span className="display-label">Training Courses</span>
                        <ul style={{ margin: "5px 0", fontSize: "13px" }}>
                          {formData.additionalDetails?.training?.filter(t => t).map((t, i) => <li key={i}>{t}</li>)}
                          {(!formData.additionalDetails?.training?.some(t => t)) && <li>N/A</li>}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 6. Work Experience */}
                  <div className="display-section">
                    <h4>6. WORK EXPERIENCE</h4>
                    <div className="table-responsive">
                      <table className="display-table">
                        <thead>
                          <tr>
                            <th>Company</th>
                            <th>Designation</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Salary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.experience?.map((exp, i) => (
                            <tr key={i}>
                              <td>{exp.company}</td>
                              <td>{exp.designation}</td>
                              <td>{exp.startDate}</td>
                              <td>{exp.endDate}</td>
                              <td>{exp.salary}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 7. Health Details */}
                  <div className="display-section">
                    <h4>7. HEALTH DETAILS</h4>
                    <p style={{ fontSize: "14px", color: "#374151" }}>{formData.healthDetails || "No health issues reported."}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIDEO TAB */}
          {activeTab === "video" && (() => {
            // Prefer the saved URL; else try the reconstructed Cloudinary path
            // (unless it already 404'd via onError).
            const effectiveUrl = interview.videoUrl || (!videoError ? interview.videoFallbackUrl : "");
            const isRecovered = !interview.videoUrl && !!effectiveUrl;
            return (
              <div className="video-tab">
                {effectiveUrl ? (
                  <div className="video-player-container">
                    <video
                      key={effectiveUrl}
                      src={effectiveUrl}
                      controls
                      className="detail-video-player"
                      onError={() => { if (!interview.videoUrl) setVideoError(true); }}
                    />
                    <a
                      href={effectiveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-external-link"
                    >
                      Open in new tab ↗
                    </a>
                    {isRecovered && (
                      <p style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: 8 }}>
                        Recovered directly from cloud storage (the link was not saved to this record).
                      </p>
                    )}
                  </div>
                ) : interview.status === "completed" ? (
                  <div className="admin-empty syncing">
                    <span className="empty-icon">⏳</span>
                    <p>Video Syncing...</p>
                    <p style={{ fontSize: "0.85rem", color: "#64748b" }}>
                      The candidate just finished and the video is still uploading in the background.
                      Please check back in a few moments or refresh.
                    </p>
                  </div>
                ) : (
                  <div className="admin-empty">
                    <span className="empty-icon">🎥</span>
                    <p>No video recording available</p>
                  </div>
                )}
              </div>
            );
          })()}
          {/* EXCEL VIDEO TAB */}
          {activeTab === "excelVideo" && (
            <div className="video-tab" style={{ textAlign: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                
                {/* 1. The Screen Recording (Primary) */}
                <div className="excel-video-section">
                  <h3 style={{ textAlign: "left", marginBottom: "12px", color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                    📹 Screen Recording
                  </h3>
                  {interview.excelVideoUrl ? (
                    <div className="video-player-container">
                      <video src={interview.excelVideoUrl} controls className="detail-video-player" />
                      <a href={interview.excelVideoUrl} target="_blank" rel="noopener noreferrer" className="video-external-link">
                        Open recording in new tab ↗
                      </a>
                    </div>
                  ) : interview.excelData ? (
                    <div className="admin-empty syncing" style={{ minHeight: "150px" }}>
                      <span className="empty-icon">⏳</span>
                      <p>Screen Recording Syncing...</p>
                      <p style={{ fontSize: "0.85rem", color: "#64748b" }}>
                        The excel data is saved, but the screen recording is still uploading. 
                        Please refresh in a minute.
                      </p>
                    </div>
                  ) : (
                    <div className="admin-empty" style={{ minHeight: "150px" }}>
                      <span className="empty-icon">🎥</span>
                      <p>No screen recording available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InterviewDetail;
